import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

import {
  type MultiselectContextValue,
  type MultiselectItemContextValue,
  type MultiselectPopoverContextValue,
} from './MultiselectInput.types';

const MultiselectContext = createContext<MultiselectContextValue | null>(null);

export interface MultiselectContextProviderProps extends PropsWithChildren {
  selected: string[];
  onSelectedChange: (selected: string[]) => void;
  disabled?: boolean;
}

export const MultiselectContextProvider = ({
  children,
  selected,
  onSelectedChange,
  disabled,
}: MultiselectContextProviderProps) => {
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggleOption = (optionId: string) => {
    const optionIsSelected = selected.includes(optionId);
    if (optionIsSelected) {
      onSelectedChange(selected.filter((id) => id !== optionId));
    } else {
      onSelectedChange([...selected, optionId]);
    }
  };

  const selectOptions = (optionIds: string[]) => {
    const selectedOptionIdsSet = new Set([...selected, ...optionIds]);
    onSelectedChange([...selectedOptionIdsSet]);
  };

  const deselectOptions = (optionIds: string[]) => {
    const optionIdsToDeselectSet = new Set(optionIds);
    const newSelected = selected.filter(
      (id) => !optionIdsToDeselectSet.has(id),
    );
    onSelectedChange(newSelected);
  };

  return (
    <MultiselectContext.Provider
      value={{
        selected,
        selectedSet,
        onSelectedChange,
        toggleOption,
        selectOptions,
        deselectOptions,
        disabled,
      }}
    >
      {children}
    </MultiselectContext.Provider>
  );
};

export const useMultiselectContext = () => {
  const context = useContext(MultiselectContext);
  if (!context) {
    throw new Error(
      'useMultiselectContext must be used within a MultiselectContextProvider',
    );
  }
  return context;
};

/**
 * Need this context to distribute the popover state as Radix UI Popover context
 * is not exposed for public use.
 */
const MultiselectPopoverContext =
  createContext<MultiselectPopoverContextValue | null>(null);

export const MultiselectPopoverContextProvider = ({
  children,
  open,
  onOpenChange,
}: MultiselectPopoverContextValue & PropsWithChildren) => {
  return (
    <MultiselectPopoverContext.Provider value={{ open, onOpenChange }}>
      {children}
    </MultiselectPopoverContext.Provider>
  );
};

export const useMultiselectPopoverContext = () => {
  const context = useContext(MultiselectPopoverContext);
  if (!context) {
    throw new Error(
      'useMultiselectPopoverContext must be used within a MultiselectPopoverContextProvider',
    );
  }
  return context;
};

const MultiselectItemContext =
  createContext<MultiselectItemContextValue | null>(null);

export const MultiselectItemContextProvider = ({
  children,
  selected,
}: MultiselectItemContextValue & PropsWithChildren) => {
  return (
    <MultiselectItemContext.Provider value={{ selected }}>
      {children}
    </MultiselectItemContext.Provider>
  );
};

export const useMultiselectItemContext = () => {
  const context = useContext(MultiselectItemContext);
  if (!context) {
    throw new Error(
      'useMultiselectItemContext must be used within a MultiselectItemContextProvider',
    );
  }
  return context;
};
